import { useCallback, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Text, Modal, useToast } from '@pairy/pds-react';
import axios from 'axios';
import { useAtomValue } from 'jotai';

import * as CommonAddClientList from '@/components/User/_components/common/AddClientList';
import { Nullable, SetState } from '@/types/helper';
import Loading from '@/components/User/_components/common/Loading';
import { Client } from '@/components/User/_components/common/CreateFileModal/index.atom';
import useFile from '@/hooks/queries/file/useFile';
import useClientFileList from '@/hooks/queries/client/useClientFileList';
import useEditFileClient from '@/hooks/mutations/file/useEditFileClient';
import useClientList from '@/hooks/queries/client/useClientList';
import { userInfoAtom } from '@/state/user/atom';

interface Props {
  isOpen: boolean;
  setIsOpen: SetState<boolean>;
  onLimitExceeded?: () => void;
}

const EditClientsModal = ({ isOpen, setIsOpen, onLimitExceeded }: Props) => {
  const router = useRouter();

  const fileId = Number(router.query.fileId);
  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
  const [addedClients, setAddedClients] = useState<Nullable<Client[]>>(null);

  const userInfo = useAtomValue(userInfoAtom);
  const toast = useToast();

  const { data: allClientsData } = useClientList(
    { sort: 'most' },
    { select: (data) => data.data.data.clients },
  );

  const clientLimitLabel = useMemo(() => {
    const planName = userInfo?.subscriptionPlan?.name;
    if (planName === 'Basic') {
      return '100';
    }
    if (planName === 'Pro' || planName === 'Pro (test)') {
      return '1,000';
    }
    if (planName === 'Pro Plus') {
      return '10,000';
    }
    return '10';
  }, [userInfo]);

  const planLimit = useMemo(() => {
    const LIMIT: Record<string, number> = {
      Free: 10,
      Basic: 100,
      Pro: 1000,
      'Pro (test)': 1000,
      'Pro Plus': 10000,
    };
    return LIMIT[userInfo?.subscriptionPlan?.name ?? 'Free'] ?? 10;
  }, [userInfo]);

  const isAtLimit =
    (allClientsData?.length ?? 0) +
      (addedClients?.filter((c) => typeof c.id === 'string').length ?? 0) >=
    planLimit;

  const handleLimitExceeded = useCallback(() => {
    const planName = userInfo?.subscriptionPlan?.name;
    const MESSAGES: Record<string, string> = {
      Free: 'Free Plan에서는 고객을 10명까지 등록 가능합니다.',
      Basic: 'Basic Plan에서는 고객을 100명까지 등록 가능합니다.',
      Pro: 'Pro Plan에서는 고객을 1,000명까지 등록 가능합니다.',
      'Pro (test)': 'Pro Plan에서는 고객을 1,000명까지 등록 가능합니다.',
    };
    if (planName === 'Pro Plus') {
      toast('고객의 등록 최대 개수를 초과했습니다.');
    } else {
      toast(
        MESSAGES[planName ?? 'Free'] ?? '고객의 등록 최대 개수를 초과했습니다.',
      );
      setIsOpen(false);
      onLimitExceeded?.();
    }
  }, [userInfo, toast, setIsOpen, onLimitExceeded]);

  const { data: fileData } = useFile(
    {
      fileId,
    },
    {
      enabled: Boolean(fileId),
      select: (data) => data.data.data.file,
    },
  );

  const { data } = useClientFileList(
    { fileId },
    {
      enabled: Boolean(fileId),
      retry: false,
      refetchOnWindowFocus: false,
      select: (data) => data.data.data.clients,
    },
  );

  const handleClickAddClient = useCallback(
    (client: Client) => {
      setAddedClients((prev) => (prev ? [...prev, client] : [client]));
      setSelectedIds((prev) => [...prev, client.id]);
    },
    [data],
  );

  const handleClickRemoveClient = useCallback(
    (id: number | string) => {
      setSelectedIds((prev) => prev.filter((prevId) => prevId !== id));
      setAddedClients((prev) => prev?.filter((client) => client.id !== id));
    },
    [addedClients],
  );

  const handleClickAddAllClients = useCallback(() => {
    if (data) {
      const filteredClients = data.filter(
        (client) => !selectedIds.includes(client.id) && !client.isSelected,
      );

      setAddedClients((prev) => [
        ...filteredClients,
        ...(prev
          ?.filter(({ isSelected }) => !isSelected)
          .map((client) => ({
            ...client,
            isAddedForm: false,
            selected: false,
          })) ?? []),
      ]);
      setSelectedIds(data.map((client) => client.id));
    }
  }, [addedClients, data]);

  const addClient = useCallback(
    (client: Client) => {
      setAddedClients((prev) => (prev ? [...prev, client] : [client]));
    },
    [addedClients, data],
  );

  const { mutate } = useEditFileClient({
    onSuccess: () => {
      router.reload();
    },
    onError: (error) => {
      setLoading(false);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        toast('고객의 등록 최대 개수를 초과했습니다.');
      }
    },
  });

  const handleClickCompleteButton = () => {
    const clientIds = addedClients
      ?.filter((client) => typeof client.id === 'number')
      .map((client) => client.id) as number[];

    const newClients = addedClients
      ?.filter((client) => typeof client.id === 'string')
      .map(({ name, email, company, phone, position, department, memo }) => ({
        name: name || undefined,
        email: email || undefined,
        company: company || undefined,
        phone: phone || undefined,
        position: position || undefined,
        department: department || undefined,
        memo: memo || undefined,
      }));

    if (newClients?.length) {
      const currentCount = allClientsData?.length ?? 0;
      if (currentCount + newClients.length > planLimit) {
        handleLimitExceeded();
        return;
      }
    }

    setLoading(true);
    mutate({
      fileId,
      clientIds: clientIds?.length ? clientIds : [],
      newClients: newClients?.length ? newClients : [],
    });
  };

  const [loading, setLoading] = useState(false);

  return (
    <Modal.Root open={isOpen} onOpenChange={setIsOpen}>
      <Modal.Content width="wide" loading={loading ? <Loading /> : undefined}>
        <Modal.Header title="자료받을 고객 더하기" />
        <Modal.Body>
          <CommonAddClientList.Root>
            <CommonAddClientList.Body>
              <CommonAddClientList.ClientTabs
                newClient={
                  <CommonAddClientList.NewClient
                    addClient={addClient}
                    clients={[...(data ?? []), ...(addedClients ?? [])]}
                    clientCount={allClientsData?.length ?? 0}
                    planLimitLabel={clientLimitLabel}
                    isAtLimit={isAtLimit}
                    onLimitExceeded={handleLimitExceeded}
                  />
                }
                existingClient={
                  <CommonAddClientList.ExistingClient
                    addAllClients={handleClickAddAllClients}
                  >
                    {data
                      ?.filter(
                        ({ id, isSelected }) =>
                          !selectedIds.includes(id) && !isSelected,
                      )
                      .map((client) => (
                        <CommonAddClientList.ClientBox
                          key={client.id}
                          email={client.email}
                          name={client.name}
                          company={client.company}
                          phone={client.phone}
                          onClick={() => handleClickAddClient(client)}
                        />
                      ))}
                    <Text variant="md-semibold" marginTop={8} marginBottom={8}>
                      이미 링크 만든 고객
                    </Text>
                    {data
                      ?.filter(({ isSelected }) => isSelected)
                      .map(({ id, name, email, company, phone }) => (
                        <CommonAddClientList.ClientBox
                          key={id}
                          email={email}
                          name={name}
                          company={company}
                          phone={phone}
                          disabled
                        />
                      ))}
                  </CommonAddClientList.ExistingClient>
                }
              />
              <CommonAddClientList.AddedClientList name={fileData?.name ?? ''}>
                {[...(addedClients ?? [])]
                  .reverse()
                  .map(({ email, id, name, company, phone }) => (
                    <CommonAddClientList.SelectedClientBox
                      key={id}
                      email={email}
                      name={name}
                      company={company}
                      phone={phone}
                      onClick={() => handleClickRemoveClient(id)}
                    />
                  ))}
              </CommonAddClientList.AddedClientList>
            </CommonAddClientList.Body>

            <CommonAddClientList.Footer
              onClick={handleClickCompleteButton}
              disabled={!addedClients || addedClients?.length === 0}
            >
              고객에게 보낼 링크 만들기
            </CommonAddClientList.Footer>
          </CommonAddClientList.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

export default EditClientsModal;
